import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import type {
    CardSettingsFormGroup,
    CatalogBasketButtonBaseSettingsFormType,
    CatalogBasketButtonFormGroup,
    CatalogBasketButtonFormType,
    CatalogBasketButtonHoverSettingsFormType,
    CatalogProductsDataFormType,
    CatalogProductsFormGroup,
    CurrentPageFormGroup,
    FilterCategoryFormGroup,
    FilterCategoryFormType,
    FilterFormGroup,
    FilterFormType,
    FilterGeneralFormGroup,
    FilterGeneralFormType,
    FilterPriceFormGroup,
    FilterPriceFormType,
    GallerySettingsFormGroup,
    GeneralSettingsFormGroup,
    ItemsInRowFormGroup,
    PagesButtonsFormGroup,
    PaginationSettingsFormGroup,
    PaginationSettingsFormType,
    SettingsFormGroup,
    SettingsFormType,
    SortingSettingsFormGroup,
    SortingSettingsFormType,
    TypographySettingsFormGroup,
} from '@web/widgets/catalog-products/type';
import { formButtonSettingsForm } from '@components/sp-form-button-settings';

export const catalogBasketButtonBaseSettingsForm = (): FormGroup<CatalogBasketButtonBaseSettingsFormType> =>
    new FormGroup<CatalogBasketButtonBaseSettingsFormType>({
        color: new FormControl(null),
        background: new FormControl(null),
        borderColor: new FormControl(null),
        borderWidth: new FormControl(null),
        borderRadius: new FormControl(null),
        borderStyle: new FormControl(null),
    });

export const catalogBasketButtonHoverSettingsForm = (): FormGroup<CatalogBasketButtonHoverSettingsFormType> =>
    new FormGroup<CatalogBasketButtonHoverSettingsFormType>({
        hoverType: new FormControl(null),
        opacity: new FormControl(null),
        shadow: new FormControl(null),
        color: new FormControl(null),
        background: new FormControl(null),
        borderColor: new FormControl(null),
    });

export const catalogBasketButtonFormGroup = (): CatalogBasketButtonFormGroup =>
    new FormGroup<CatalogBasketButtonFormType>({
        label: new FormControl(null, [Validators.required]),
        size: new FormControl(null),
        styleType: new FormControl(null),
        action: new FormControl(null),
        buttonBase: catalogBasketButtonBaseSettingsForm(),
        buttonHover: catalogBasketButtonHoverSettingsForm(),
        isShowButton: new FormControl(null),
        buttonIcon: new FormControl(null),
    });

export const itemsInRowFormGroup = (): ItemsInRowFormGroup =>
    new FormGroup({
        desktop: new FormControl(null),
        mobile: new FormControl(null),
    });

export const pagesButtonsFormGroup = (): PagesButtonsFormGroup =>
    new FormGroup({
        textColor: new FormControl(null),
        backgroundColor: new FormControl(null),
        borderColor: new FormControl(null),
        borderRadius: new FormControl(null),
        inputSize: new FormControl(null),
    });

export const currentPageFormGroup = (): CurrentPageFormGroup =>
    new FormGroup({
        textColor: new FormControl(null),
        borderColor: new FormControl(null),
        backgroundColor: new FormControl(null),
    });

export const paginationSettingsFormGroup = (): PaginationSettingsFormGroup =>
    new FormGroup<PaginationSettingsFormType>({
        pagesButtons: pagesButtonsFormGroup(),
        currentPage: currentPageFormGroup(),
        nextPage: formButtonSettingsForm(),
    });

export const sortingSettingsFormGroup = (): SortingSettingsFormGroup =>
    new FormGroup<SortingSettingsFormType>({
        enable: new FormControl(null),
        textColor: new FormControl(null),
        borderColor: new FormControl(null),
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        borderStyle: new FormControl(null),
        inputSize: new FormControl(null),
        searchEnable: new FormControl(null),
        searchPlaceholder: new FormControl(null),
    });

export const generalSettingsFormGroup = (): GeneralSettingsFormGroup =>
    new FormGroup({
        margin: new FormControl(null),
    });

export const gallerySettingsFormGroup = (): GallerySettingsFormGroup =>
    new FormGroup({
        aspectRatio: new FormControl(null),
        attachment: new FormControl(null),
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        margin: new FormControl(null),
    });

export const cardSettingsFormGroup = (): CardSettingsFormGroup =>
    new FormGroup({
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        padding: new FormControl(null),
        isBorder: new FormControl(null),
        borderWidth: new FormControl(null),
        borderStyle: new FormControl(null),
        borderColor: new FormControl(null),
    });

const baseTypographyFormGroup = () =>
    new FormGroup({
        color: new FormControl(null),
        fontSize: new FormControl(null),
        fontStyle: new FormArray([]),
        isShow: new FormControl(null),
    });

export const typographySettingsFormGroup = (): TypographySettingsFormGroup =>
    new FormGroup({
        titleSettings: baseTypographyFormGroup(),
        counterSettings: baseTypographyFormGroup(),
        priceSettings: baseTypographyFormGroup(),
        oldPriceSettings: baseTypographyFormGroup(),
    });

export const settingsFormGroup = (): SettingsFormGroup =>
    new FormGroup<SettingsFormType>({
        general: generalSettingsFormGroup(),
        card: cardSettingsFormGroup(),
        gallery: gallerySettingsFormGroup(),
        typography: typographySettingsFormGroup(),
    });

export const selectFilterSettingsFormGroup = (): FilterCategoryFormType['selectFilterSettings'] =>
    new FormGroup({
        textColor: new FormControl(null),
        borderColor: new FormControl(null),
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        borderStyle: new FormControl(null),
        inputSize: new FormControl(null),
    });

export const filterCategoryFormGroup = (): FilterCategoryFormGroup =>
    new FormGroup<FilterCategoryFormType>({
        label: new FormControl(null),
        categoryLabelFont: baseTypographyFormGroup(),
        categoryFont: baseTypographyFormGroup(),
        selectedCategoryFont: baseTypographyFormGroup(),
        selectFilterSettings: selectFilterSettingsFormGroup(),
    });

export const filterGeneralFormGroup = (): FilterGeneralFormGroup =>
    new FormGroup<FilterGeneralFormType>({
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        isBorder: new FormControl(null),
        borderWidth: new FormControl(null),
        borderStyle: new FormControl(null),
        borderColor: new FormControl(null),
        paddingTop: new FormControl(null),
        paddingBottom: new FormControl(null),
        paddingLeft: new FormControl(null),
        paddingRight: new FormControl(null),
    });

export const filterPriceFormGroup = (): FilterPriceFormGroup =>
    new FormGroup<FilterPriceFormType>({
        label: new FormControl(null),
        priceLabelFont: baseTypographyFormGroup(),
        borderStyle: new FormControl(null),
        textColor: new FormControl(null),
        backgroundColor: new FormControl(null),
        borderColor: new FormControl(null),
        borderRadius: new FormControl(null),
        inputSize: new FormControl(null),
        slider: new FormControl(null),
        colorLine: new FormControl(null),
        colorFilledLine: new FormControl(null),
        colorButtons: new FormControl(null),
    });

export const filterSettingsFormGroup = (): FilterFormGroup =>
    new FormGroup<FilterFormType>({
        general: filterGeneralFormGroup(),
        category: filterCategoryFormGroup(),
        price: filterPriceFormGroup(),
    });

export const catalogProductsWidgetDataFormGroup = (): CatalogProductsFormGroup =>
    new FormGroup<CatalogProductsDataFormType>({
        selectedCategory: new FormControl(null),
        catalogView: new FormControl(null),
        itemsInRow: itemsInRowFormGroup(),
        maxPerPage: new FormControl(null),
        basketButton: catalogBasketButtonFormGroup(),
        pagination: paginationSettingsFormGroup(),
        sorting: sortingSettingsFormGroup(),
        settings: settingsFormGroup(),
        filter: filterSettingsFormGroup(),
        userHash: new FormControl(null),
    });
